import { useModal } from "@ebay/nice-modal-react";
import { useQuery } from "@tanstack/react-query";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { useServerTable } from "../../../components/table/tableFeatures";
import { differenceInDays, endOfMonth, formatISO, isAfter, isSameMonth, startOfDay, startOfMonth } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaPencil } from "react-icons/fa6";
import { css } from "../../../../styled-system/css";
import { Box, VStack, Wrap } from "../../../../styled-system/jsx";
import { Accordion } from "../../../components/core/basic/Accordion";
import { Button } from "../../../components/core/basic/Button";
import DetailedMetric from "../../../components/core/blocks/DetailedMetric";
import { DevErrorDisplay } from "../../../components/core/blocks/DevErrorDisplay";
import { TableErrorWrapper } from "../../../components/table/TableErrorWrapper";
import Loading from "../../../components/core/blocks/Loading";
import { CategoryCell } from "../../../components/custom/merchandise/cells/CategoryCell";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import useQueryParams from "../../../hooks/useQueryParams";
import { useAppSelector } from "../../../redux/hooks";
import { formatCurrency, formatPercent } from "../../../utils/formatNumber";
import type { CategoryGoal, OrgGoal } from "../../../zodios/apiDefinitions/goals";
import {
  useGetV4CategoryReportByCategory,
  type CategoryReportByCategoryRow,
} from "../../perf_reports/api/category/v4CategoryReportByCategory";
import { useGetV4CategoryReportTotals } from "../../perf_reports/api/category/v4CategoryReportTotals";
import { useCategoryGoalsQueryOptions } from "../api/useCategoryGoalsQueryOptions";
import { useOrgGoalsQueryOptions } from "../api/useOrgGoalsQueryOptions";
import { AdSpendChart } from "../charts/AdSpendChart";
import { GoalProgressBar } from "../charts/GoalProgressBar";
import { PacingGauge } from "../charts/PacingGauge";
import { TacosChart } from "../charts/TacosChart";
import { TotalSalesChart } from "../charts/TotalSalesChart";
import { CategoryGoalsConfigModal } from "../GoalsConfigurationModal";
import { useSimpleCan } from "../../../config/casl/casl";

export function DetailsTab({ displayPeriod }: { displayPeriod: Date }) {
  const { t } = useTranslation(["goals"]);
  const startDate = startOfMonth(displayPeriod);
  const endDate = isSameMonth(displayPeriod, new Date()) ? startOfDay(new Date()) : endOfMonth(displayPeriod);
  const [, setTab] = useQueryParams("tab", "details");

  const orgGoalQuery = useQuery(
    useOrgGoalsQueryOptions(
      formatISO(startDate, {
        representation: "date",
      }),
      formatISO(endDate, {
        representation: "date",
      }),
    ),
  );
  const totalsQuery = useQuery(
    useGetV4CategoryReportTotals()({ start: startDate, end: endDate, preStart: startDate, preEnd: endDate }),
  );
  if (totalsQuery.isLoading || orgGoalQuery.isLoading) {
    return <Loading />;
  }

  if (!totalsQuery.isSuccess) {
    return <DevErrorDisplay userMessage={t(($) => $.goals["Error loading stats"])} error={totalsQuery.error} />;
  }

  if (!orgGoalQuery.isSuccess) {
    return (
      <DevErrorDisplay userMessage={t(($) => $.goals["Error loading organization goals"])} error={orgGoalQuery.error} />
    );
  }

  const goalBag = getGoalBag(orgGoalQuery.data, displayPeriod);
  return !goalBag ?
      <VStack>
        <p>{t(($) => $.goals["No goals set for this period"])}</p>
        <Button onPress={() => setTab("config")}>{t(($) => $.goals["Configure Goals"])}</Button>
      </VStack>
    : <>
        <Accordion title={t(($) => $.goals["Sales Goals"])} defaultExpanded>
          <Wrap gap="medium">
            <Box flex={1} minWidth={"300px"}>
              <GoalProgressBar
                current={totalsQuery.data.data.total_sales ?? 0}
                totalGoal={goalBag.totalGoal.totalSales}
                todayGoal={goalBag.expectedToDate.totalSales}
              />
              <Wrap justifyContent={"space-evenly"} width="100%">
                <DetailedMetric
                  center
                  metric={{
                    id: "goal",
                    name: t(($) => $.goals["Goal"]),
                    format: "currency",
                    compact: false,
                    accessor: (data) => data?.value ?? 0,
                  }}
                  data={{ value: goalBag.totalGoal.totalSales }}
                />
                <DetailedMetric
                  center
                  metric={{
                    id: "actual",
                    name: t(($) => $.goals["Actual"]),
                    defaultCompare: "prev",
                    format: "currency",
                    compact: false,
                    compareHighlight: "neutral",
                    accessor: (data) => data?.value ?? 0,
                    prev_accessor: (data) => data?.target ?? 0,
                    percent_accessor: (data) => (data?.value ?? 0) / (data?.target ?? 1) - 1,
                    delta_accessor: (data) => (data?.value ?? 0) - (data?.target ?? 0),
                  }}
                  data={{
                    value: totalsQuery.data.data.total_sales ?? 0,
                    target: goalBag.expectedToDate.totalSales,
                  }}
                  showTarget
                  targetLabel={t(($) => $.goals["vs. Expected"])}
                />
              </Wrap>
            </Box>
            <Box flex={2}>
              <TotalSalesChart
                startDate={startDate}
                endDate={endDate}
                dailySalesGoal={goalBag.dailyGoal.totalSales}
                daysInPeriod={goalBag.daysInPeriod}
              />
            </Box>
          </Wrap>
        </Accordion>
        <Accordion title={t(($) => $.goals["Ad Spend Budget"])} defaultExpanded>
          <Wrap gap="medium">
            <VStack flex={1} gap="medium">
              <AdSpendChart
                startDate={startDate}
                endDate={endDate}
                dailyAdSpendGoal={goalBag.dailyGoal.adSpend}
                daysInPeriod={goalBag.daysInPeriod}
              />
              <PacingGauge
                target={goalBag.expectedToDate.adSpend}
                current={totalsQuery.data.data.cost ?? 0}
                formatOptions={{
                  format: "currency",
                  compact: true,
                  decimalPlaces: 0,
                }}
                allowance={0.5}
              />
              <Wrap justifyContent={"space-evenly"} width="100%">
                <DetailedMetric
                  center
                  metric={{
                    id: "adSpendGoal",
                    name: t(($) => $.goals["Goal"]),
                    format: "currency",
                    compact: false,
                    accessor: (data) => data?.value ?? 0,
                  }}
                  data={{ value: goalBag.totalGoal.adSpend }}
                />
                <DetailedMetric
                  center
                  metric={{
                    id: "adSpendActual",
                    name: t(($) => $.goals["Actual"]),
                    defaultCompare: "prev",
                    format: "currency",
                    compact: false,
                    compareHighlight: "neutral",
                    accessor: (data) => data?.value ?? 0,
                    prev_accessor: (data) => data?.target ?? 0,
                    percent_accessor: (data) => (data?.value ?? 0) / (data?.target ?? 1) - 1,
                    delta_accessor: (data) => (data?.value ?? 0) - (data?.target ?? 0),
                  }}
                  data={{
                    value: totalsQuery.data.data.cost ?? 0,
                    target: goalBag.expectedToDate.adSpend,
                  }}
                  showTarget
                  targetLabel={t(($) => $.goals["vs. Expected"])}
                />
              </Wrap>
            </VStack>
            <VStack flex={1} gap="medium">
              <TacosChart
                startDate={startDate}
                endDate={endDate}
                tacosGoal={goalBag.expectedToDate.adSpend / goalBag.expectedToDate.totalSales}
                daysInPeriod={goalBag.daysInPeriod}
              />
              <PacingGauge
                target={goalBag.expectedToDate.adSpend / goalBag.expectedToDate.totalSales}
                current={totalsQuery.data.data.total_acos ?? 0}
                formatOptions={{
                  format: "percent",
                  decimalPlaces: 0,
                }}
              />
              <Wrap justifyContent={"space-evenly"} width="100%">
                <DetailedMetric
                  center
                  metric={{
                    id: "tacosGoal",
                    name: t(($) => $.goals["Goal"]),
                    format: "percent",
                    accessor: (data) => data?.value ?? 0,
                  }}
                  data={{ value: goalBag.totalGoal.tacos }}
                />
                <DetailedMetric
                  center
                  metric={{
                    id: "tacosActual",
                    name: t(($) => $.goals["Actual"]),
                    format: "percent",
                    accessor: (data) => data?.value ?? 0,
                  }}
                  data={{ value: totalsQuery.data.data.total_acos ?? 0 }}
                />
              </Wrap>
            </VStack>
          </Wrap>
        </Accordion>
        <Accordion title={t(($) => $.goals["Category Goals"])} defaultExpanded>
          <CategoryTable startDate={startDate} endDate={endDate} displayPeriod={displayPeriod} />
        </Accordion>
      </>;
}

function getCurrentPeriodGoal(config: OrgGoal[] | undefined, displayPeriod: Date) {
  const goalKey = formatISO(new Date(displayPeriod.getFullYear(), displayPeriod.getMonth(), 1), {
    representation: "date",
  });
  const goalValue = config?.find((goal) => goal.start_date === goalKey);
  return goalValue ?? null;
}

function PacingGoalDisplay({ pacing }: { pacing: number }) {
  return (
    <span
      className={css({
        borderColor:
          pacing < 0.5 ? "trellisPalette.17"
          : pacing < 0.75 ? "trellisPalette.16"
          : "trellisPalette.12",
        borderRadius: "pill",
        borderWidth: "2px",
        paddingX: "xsmall",
        backgroundColor:
          pacing < 0.5 ? "trellisPalette.17/10"
          : pacing < 0.75 ? "trellisPalette.16/10"
          : "trellisPalette.12/10",
        borderStyle: "solid",
        whiteSpace: "nowrap",
      })}
    >
      <span>{pacing > 1 ? "🔥 " : null}</span>
      {formatPercent(pacing)}
    </span>
  );
}

function PacingRangeDisplay({ pacing }: { pacing: number }) {
  return (
    <span
      className={css({
        borderColor:
          pacing < 0.5 ? "trellisPalette.17"
          : pacing < 0.85 ? "trellisPalette.16"
          : pacing > 1.5 ? "trellisPalette.17"
          : pacing > 1.15 ? "trellisPalette.16"
          : "trellisPalette.12",
        borderRadius: "pill",
        borderWidth: "2px",
        paddingX: "xsmall",
        backgroundColor:
          pacing < 0.5 ? "trellisPalette.17/10"
          : pacing < 0.85 ? "trellisPalette.16/10"
          : pacing > 1.5 ? "trellisPalette.17/10"
          : pacing > 1.15 ? "trellisPalette.16/10"
          : "trellisPalette.12/10",
        borderStyle: "solid",
        whiteSpace: "nowrap",
      })}
    >
      {formatPercent(pacing)}
    </span>
  );
}

const columnHelper = createColumnBuilder<CategoryReportByCategoryRow>();

function CategoryTable({ startDate, endDate, displayPeriod }: { startDate: Date; endDate: Date; displayPeriod: Date }) {
  const { t } = useTranslation(["goals"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const userCanUpdateGoals = useSimpleCan("update", "Goal");
  const { state, initialState, setters } = usePersistedTableState({
    persistKey: "categoryGoals",
    initialState: { sorting: [{ id: "total_sales", desc: true }] },
  });

  const getCategoryReport = useGetV4CategoryReportByCategory();
  const categoryReportQuery = useBlockWindowQuery(getCategoryReport, {
    pagination: state.pagination,
    sorting: state.sorting,
    start: startDate,
    end: endDate,
    preStart: startDate,
    preEnd: endDate,
  });

  const categoryGoalsQuery = useQuery({
    ...useCategoryGoalsQueryOptions(
      formatISO(startDate, { representation: "date" }),
      formatISO(endDate, { representation: "date" }),
    ),
    select(data) {
      return data.reduce(
        (acc, goal) => {
          if (!acc[goal.category_id]) {
            acc[goal.category_id] = [];
          }
          acc[goal.category_id]!.push(goal);
          return acc;
        },
        {} as Record<number, CategoryGoal[]>,
      );
    },
  });

  const columns = useMemo(
    () =>
      (() => [
        columnHelper.accessor("category_name", {
          header: t(($) => $.goals["Category"]),
          cell: ({ row }) => <CategoryCell original={row.original} />,
          minSize: 200,
        }),
        columnHelper.accessor("total_sales", {
          header: t(($) => $.goals["Total Sales"]),
          cell: ({ getValue, row }) => {
            const value = getValue();
            const toDateGoal =
              getGoalBag(categoryGoalsQuery.data?.[row.original.category_id], displayPeriod)?.expectedToDate
                .totalSales ?? 0;
            if (typeof value !== "number") {
              return "-";
            }
            return (
              <VStack
                gap={"small"}
                alignItems={"end"}
                width={"100%"}
                paddingY="xsmall"
                paddingX={"small"}
                lineHeight={"1"}
              >
                <span>
                  {formatCurrency(getValue() ?? 0, {
                    marketPlace: marketPlace,
                  })}
                </span>
                <span>
                  {toDateGoal ?
                    <>
                      {t(($) => $.goals["Pacing:"])} <PacingGoalDisplay pacing={value / toDateGoal} />
                    </>
                  : null}
                </span>
              </VStack>
            );
          },
          minSize: 200,
        }),
        columnHelper.accessor("cost", {
          header: t(($) => $.goals["Ad Spend"]),
          cell: ({ getValue, row }) => {
            const value = getValue();
            const toDateGoal =
              getGoalBag(categoryGoalsQuery.data?.[row.original.category_id], displayPeriod)?.expectedToDate.adSpend ??
              0;
            if (typeof value !== "number") {
              return "-";
            }
            return (
              <VStack
                gap={"small"}
                alignItems={"end"}
                width={"100%"}
                paddingY="xsmall"
                paddingX={"small"}
                lineHeight={"1"}
              >
                <span>
                  {formatCurrency(value, {
                    marketPlace: marketPlace,
                  })}
                </span>
                {toDateGoal ?
                  <span>
                    {t(($) => $.goals["Pacing:"])} <PacingRangeDisplay pacing={value / toDateGoal} />
                  </span>
                : null}
              </VStack>
            );
          },
          minSize: 200,
        }),
        columnHelper.accessor("total_acos", {
          header: t(($) => $.goals["TACOS"]),
          cell: ({ getValue }) => formatPercent(getValue() ?? 0),
        }),
        columnHelper.accessor(
          (row) => getGoalBag(categoryGoalsQuery.data?.[row.category_id], displayPeriod)?.totalGoal.totalSales,
          {
            id: "total_sales_goal",
            header: t(($) => $.goals["Total Sales Goal"]),
            cell: function Cell({ row }) {
              const goalBag = getGoalBag(categoryGoalsQuery.data?.[row.original.category_id], displayPeriod);
              const value = goalBag?.["totalGoal"].totalSales;
              return value ?
                  formatCurrency(value, {
                    marketPlace: marketPlace,
                  })
                : t(($) => $.goals["N/A"]);
            },
            enableSorting: false,
          },
        ),
        columnHelper.accessor(
          (row) => getGoalBag(categoryGoalsQuery.data?.[row.category_id], displayPeriod)?.totalGoal.adSpend,
          {
            id: "ad_spend_goal",
            header: t(($) => $.goals["Ad Spend Goal"]),
            cell: function Cell({ row }) {
              const goalBag = getGoalBag(categoryGoalsQuery.data?.[row.original.category_id], displayPeriod);
              const value = goalBag?.["totalGoal"].adSpend;
              return value ?
                  formatCurrency(value, {
                    marketPlace: marketPlace,
                  })
                : t(($) => $.goals["N/A"]);
            },
            enableSorting: false,
          },
        ),
        columnHelper.accessor(
          (row) => {
            const goalBag = getGoalBag(categoryGoalsQuery.data?.[row.category_id], displayPeriod);
            if (!goalBag) {
              return null;
            }
            return goalBag.totalGoal.adSpend / goalBag.totalGoal.totalSales;
          },
          {
            id: "tacos_goal",
            header: t(($) => $.goals["TACOS Goal"]),
            cell: ({ getValue }) => {
              const value = getValue();
              return value ? formatPercent(value) : t(($) => $.goals["N/A"]);
            },
            enableSorting: false,
          },
        ),

        columnHelper.display({
          id: "actions",
          header: "",
          size: 50,
          enableResizing: false,

          cell: function Cell({ row }) {
            const modal = useModal(CategoryGoalsConfigModal);
            if (!userCanUpdateGoals) {
              return null;
            }

            return (
              <Button
                variant="contentOnly"
                onPress={() => {
                  void modal.show({ categoryId: row.original.category_id });
                }}
              >
                <FaPencil />
              </Button>
            );
          },
          meta: {
            justify: "center",
          },
        }),
      ])(),
    [t, marketPlace, displayPeriod, categoryGoalsQuery.data, userCanUpdateGoals],
  );

  const table = useServerTable({
    data: categoryReportQuery.data?.data,
    columns,
    state,
    ...setters,
    initialState,
    rowCount: categoryReportQuery.data?.count ?? 0,
    getRowCanExpand: () => true,
    meta: {
      showSkeleton:
        categoryReportQuery.isFetching && (!categoryReportQuery.data || categoryReportQuery.isPlaceholderData),
    },
  });

  return (
    <TableErrorWrapper
      error={categoryReportQuery.error}
      userMessage={t(($) => $.goals["Error loading category stats"])}
    >
      <Wrap>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </Wrap>
      <Table
        table={table}
        renderExpander={function Expander(row) {
          const goalBag = getGoalBag(categoryGoalsQuery.data?.[row.original.category_id], displayPeriod);
          if (!goalBag) {
            return <p>{t(($) => $.goals["No goal set for this category"])}</p>;
          }
          return (
            <>
              <Accordion title={t(($) => $.goals["Sales Goals"])} defaultExpanded>
                <Wrap gap="medium">
                  <Box flex={1} minWidth={"300px"}>
                    <GoalProgressBar
                      current={row.original.total_sales ?? 0}
                      totalGoal={goalBag.totalGoal.totalSales}
                      todayGoal={goalBag.expectedToDate.totalSales}
                    />
                    <Wrap justifyContent={"space-evenly"} width="100%">
                      <DetailedMetric
                        center
                        metric={{
                          id: "categoryGoal",
                          name: t(($) => $.goals["Goal"]),
                          format: "currency",
                          compact: false,
                          accessor: (data) => data?.value ?? 0,
                        }}
                        data={{ value: goalBag.totalGoal.totalSales }}
                      />
                      <DetailedMetric
                        center
                        metric={{
                          id: "categoryActual",
                          name: t(($) => $.goals["Actual"]),
                          defaultCompare: "prev",
                          format: "currency",
                          compact: false,
                          compareHighlight: "neutral",
                          accessor: (data) => data?.value ?? 0,
                          prev_accessor: (data) => data?.target ?? 0,
                          percent_accessor: (data) => (data?.value ?? 0) / (data?.target ?? 1) - 1,
                          delta_accessor: (data) => (data?.value ?? 0) - (data?.target ?? 0),
                        }}
                        data={{
                          value: row.original.total_sales ?? 0,
                          target: goalBag.expectedToDate.totalSales,
                        }}
                        showTarget
                        targetLabel={t(($) => $.goals["vs. Expected"])}
                      />
                    </Wrap>
                  </Box>
                  <Box flex={2}>
                    <TotalSalesChart
                      startDate={startDate}
                      endDate={endDate}
                      dailySalesGoal={goalBag.dailyGoal.totalSales}
                      daysInPeriod={goalBag.daysInPeriod}
                      categoryId={row.original.category_id}
                    />
                  </Box>
                </Wrap>
              </Accordion>
              <Accordion title={t(($) => $.goals["Ad Spend Budget"])} defaultExpanded>
                <Wrap gap="medium">
                  <VStack flex={1} gap="medium">
                    <AdSpendChart
                      startDate={startDate}
                      endDate={endDate}
                      dailyAdSpendGoal={goalBag.dailyGoal.adSpend}
                      daysInPeriod={goalBag.daysInPeriod}
                      categoryId={row.original.category_id}
                    />
                    <PacingGauge
                      target={goalBag.expectedToDate.adSpend}
                      current={row.original.cost ?? 0}
                      formatOptions={{
                        format: "currency",
                        compact: true,
                        decimalPlaces: 0,
                      }}
                      allowance={0.5}
                    />
                    <Wrap justifyContent={"space-evenly"} width="100%">
                      <DetailedMetric
                        center
                        metric={{
                          id: "categoryAdSpendGoal",
                          name: t(($) => $.goals["Goal"]),
                          format: "currency",
                          compact: false,
                          accessor: (data) => data?.value ?? 0,
                        }}
                        data={{ value: goalBag.totalGoal.adSpend }}
                      />
                      <DetailedMetric
                        center
                        metric={{
                          id: "categoryAdSpendActual",
                          name: t(($) => $.goals["Actual"]),
                          defaultCompare: "prev",
                          format: "currency",
                          compact: false,
                          compareHighlight: "neutral",
                          accessor: (data) => data?.value ?? 0,
                          prev_accessor: (data) => data?.target ?? 0,
                          percent_accessor: (data) => (data?.value ?? 0) / (data?.target ?? 1) - 1,
                          delta_accessor: (data) => (data?.value ?? 0) - (data?.target ?? 0),
                        }}
                        data={{
                          value: row.original.cost ?? 0,
                          target: goalBag.expectedToDate.adSpend,
                        }}
                        showTarget
                        targetLabel={t(($) => $.goals["vs. Expected"])}
                      />
                    </Wrap>
                  </VStack>
                  <VStack flex={1} gap="medium">
                    <TacosChart
                      startDate={startDate}
                      endDate={endDate}
                      tacosGoal={goalBag.expectedToDate.adSpend / goalBag.expectedToDate.totalSales}
                      daysInPeriod={goalBag.daysInPeriod}
                      categoryId={row.original.category_id}
                    />
                    <PacingGauge
                      target={goalBag.expectedToDate.adSpend / goalBag.expectedToDate.totalSales}
                      current={row.original.total_acos ?? 0}
                      formatOptions={{
                        format: "percent",
                        decimalPlaces: 0,
                      }}
                    />
                    <Wrap justifyContent={"space-evenly"} width="100%">
                      <DetailedMetric
                        center
                        metric={{
                          id: "categoryTacosGoal",
                          name: t(($) => $.goals["Goal"]),
                          format: "percent",
                          accessor: (data) => data?.value ?? 0,
                        }}
                        data={{ value: goalBag.totalGoal.tacos }}
                      />
                      <DetailedMetric
                        center
                        metric={{
                          id: "categoryTacosActual",
                          name: t(($) => $.goals["Actual"]),
                          format: "percent",
                          accessor: (data) => data?.value ?? 0,
                        }}
                        data={{ value: row.original.total_acos ?? 0 }}
                      />
                    </Wrap>
                  </VStack>
                </Wrap>
              </Accordion>
            </>
          );
        }}
      />
    </TableErrorWrapper>
  );
}

function getGoalBag(goalConfig: OrgGoal[] | undefined, displayPeriod: Date) {
  const isCurrentPeriod = isSameMonth(displayPeriod, new Date());
  const isFuturePeriod = !isCurrentPeriod && isAfter(displayPeriod, new Date());
  const currentGoal = getCurrentPeriodGoal(goalConfig, displayPeriod);

  if (!currentGoal) {
    return null;
  }

  const startDate = startOfMonth(displayPeriod);
  const endDate = endOfMonth(displayPeriod);

  const daysInPeriod = differenceInDays(endDate, startDate) + 1;
  const dailySalesGoal = currentGoal.total_sales_goal / daysInPeriod;
  const dailyAdSpendGoal = currentGoal.ad_spend_goal / daysInPeriod;

  const expectedSalesToDate =
    isCurrentPeriod ? dailySalesGoal * differenceInDays(new Date(), startDate)
    : isFuturePeriod ? 0
    : currentGoal.total_sales_goal;
  const expectedAdSpendToDate =
    isCurrentPeriod ? dailyAdSpendGoal * differenceInDays(new Date(), startDate)
    : isFuturePeriod ? 0
    : currentGoal.ad_spend_goal;

  return {
    expectedToDate: {
      totalSales: expectedSalesToDate,
      adSpend: expectedAdSpendToDate,
    },
    dailyGoal: {
      totalSales: dailySalesGoal,
      adSpend: dailyAdSpendGoal,
    },
    totalGoal: {
      totalSales: currentGoal.total_sales_goal,
      adSpend: currentGoal.ad_spend_goal,
      tacos: currentGoal.ad_spend_goal / currentGoal.total_sales_goal,
    },
    daysInPeriod,
  };
}
